import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

const RELOAD_KEY = "chunk_reload_ts";
const COOLDOWN_MS = 15_000; // prevent reload loops — at most once per 15 seconds

function canReload(): boolean {
  try {
    const ts = sessionStorage.getItem(RELOAD_KEY);
    if (!ts) return true;
    return Date.now() - Number(ts) > COOLDOWN_MS;
  } catch {
    return false;
  }
}

/**
 * Load the newest build: reload the page -- at most once per COOLDOWN_MS, so a
 * file that is genuinely broken can never loop. False when it may not (just
 * reloaded), and the caller lets the error surface instead.
 */
export function reloadForNewBuild(): boolean {
  if (!canReload()) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // ignore storage errors in restricted environments
  }
  reloading = true;
  window.location.reload();
  return true;
}

// Set once a reload into the new build is under way. Until the page goes,
// any page import may still fail on its way out -- e.g. a named-export picker
// `import(...).then((m) => ({ default: m.Page }))` reading `.Page` of the
// empty module -- and none of that should reach the error screen.
let reloading = false;

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("error loading dynamically imported module") ||
    error.message.includes("Importing a module script failed") ||
    // Vite: a page's stylesheet from the old build is gone.
    error.message.includes("Unable to preload CSS") ||
    // The old file's URL now answers with the app's HTML page.
    error.message.includes("Failed to load module script")
  );
}

// The page while the reload into the new build happens.
const Reloading = () => null;

/**
 * Drop-in replacement for React.lazy() that silently reloads the page when a
 * chunk fails to load after a new deployment. The reload happens inside the
 * factory — before React sees an error — so no error boundary or error UI is
 * ever shown to the user. They just see the normal loading spinner.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  // SUPERSEDED: returned factory()'s result as it was. When main.tsx's
  // vite:preloadError handler takes a failure, Vite resolves the import to
  // `undefined` instead of rejecting -- so nothing was caught here, and React
  // read `.default` of it: "Cannot read properties of undefined (reading
  // 'default')" after every deploy.
  return lazy(async () => {
    try {
      const module = await factory();
      // Empty: main.tsx has already started the reload into the new build.
      if (!module) return { default: Reloading as unknown as T };
      return module;
    } catch (error) {
      // The page is already reloading into the new build: whatever failed is
      // the old build's, so stay on the loading state instead of erroring.
      if (reloading) return { default: Reloading as unknown as T };
      if (isChunkLoadError(error) && reloadForNewBuild()) {
        // Return a no-op component — the page will reload before this renders.
        return { default: Reloading as unknown as T };
      }
      throw error;
    }
  });
}
